import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import Button from './ui/FitButton';
import SingleLineText from './ui/SingleLineText';
import { getAdminPlatformActivity } from '../api';

const SERIES = [
    { key: 'ios', label: 'iOS', color: '#2563eb' },
    { key: 'android', label: 'Android', color: '#16a34a' },
];
const number = value => Number(value).toLocaleString('ko-KR');
const percent = value => value == null ? '—' : `${Number(value).toFixed(1)}%`;

function TodayRatio({ today, yesterday, dayLabel }) {
    const iosRatio = today.mobile > 0 ? today.ios / today.mobile * 100 : 0;
    return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '120px 1fr', sm: '170px 1fr' }, gap: { xs: 2, sm: 3 }, alignItems: 'center', my: 2 }}>
        <svg viewBox="0 0 140 140" role="img" aria-label={today.mobile > 0 ? `${dayLabel} 모바일 접속 비율: iOS ${percent(today.iosPercent)}, Android ${percent(today.androidPercent)}` : `${dayLabel} 모바일 접속 기록 없음`} style={{ display: 'block', width: '100%' }}>
            <circle cx="70" cy="70" r="54" fill="none" stroke="#e5e7eb" strokeWidth="14" />
            {today.mobile > 0 && <g transform="rotate(-90 70 70)">
                <circle cx="70" cy="70" r="54" fill="none" stroke={SERIES[1].color} strokeWidth="14" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={SERIES[0].color} strokeWidth="14" pathLength="100" strokeDasharray={`${iosRatio} ${100 - iosRatio}`} />
            </g>}
            <text x="70" y="65" textAnchor="middle" fontSize="12" fill="#666">{dayLabel} 앱 접속</text>
            <text x="70" y="90" textAnchor="middle" fontSize="22" fontWeight="800" fill="#1a1a1a">{number(today.mobile)}대</text>
        </svg>
        <Box sx={{ display: 'grid', gap: 2 }}>
            {SERIES.map(({ key, label, color }) => <Box key={key} data-testid={`platform-today-${key}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{label}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', sm: '1.6rem' }, mt: 0.25 }}>{percent(today[`${key}Percent`])} <Box component="span" sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#666' }}>· {number(today[key])}대</Box></Typography>
                {yesterday && <Typography sx={{ color: '#777', fontSize: '0.8rem' }}>{dayLabel === '오늘' ? '어제' : '그 전날'} {number(yesterday[key])}대</Typography>}
            </Box>)}
        </Box>
    </Box>;
}

export default function AdminPlatformActivity({ days = 7, includeToday = true, revision = 0, onRetry, LineChartComponent }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    useEffect(() => {
        let live = true;
        setLoading(true);
        setError(false);
        getAdminPlatformActivity(days, includeToday).then(result => {
            if (!result?.today || !Array.isArray(result.trend) || !result.trend.length) throw new Error('접속 집계 응답 없음');
            if (live) setReport(result);
        }).catch(() => { if (live) setError(true); }).finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [days, includeToday, revision]);
    const selectedDay = report?.trend[0];
    const dayLabel = includeToday ? '오늘' : '어제';

    return <Box component="section" aria-label="운영체제별 접속 현황" sx={{ mb: 4 }}>
        <SingleLineText component="h2" sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 0.75 }}>{dayLabel} 접속 · iOS와 Android</SingleLineText>
        <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 1.5 }}>운영체제별 접속 수를 함께 보면 한쪽의 접속 감소를 확인할 수 있어요.</Typography>
        {loading ? <Typography role="status" sx={{ py: 3, color: '#666' }}>접속 현황을 불러오고 있어요…</Typography> : error ? <Alert severity="error" sx={{ my: 2 }} action={onRetry && <Button onClick={onRetry}>다시 시도</Button>}>접속 현황을 불러오지 못했어요. 상단 새로고침으로 다시 확인해주세요.</Alert> : report && <>
            <Typography sx={{ fontSize: '0.8rem', color: '#777', mt: 1.5 }}>{selectedDay.date} · 한국 시간 · {report.generatedAt?.replace('T', ' ').slice(11, 16)} 집계</Typography>
            <TodayRatio today={selectedDay} yesterday={report.trend[1]} dayLabel={dayLabel} />
            {selectedDay.mobile === 0 && <Typography sx={{ color: '#666', mb: 1 }}>{dayLabel} 기록된 iOS·Android 접속이 없어요.</Typography>}
            <Typography sx={{ fontSize: '0.8rem', color: '#777', mb: 2 }}>전체 {number(selectedDay.total)}대 · 웹 {number(selectedDay.web)}대 · 미확인 {number(selectedDay.unknown)}대</Typography>
            <Box data-testid="platform-activity-trend">
                <LineChartComponent title="운영체제별 일별 접속 수" subtitle={`최근 ${days}일 · ${includeToday ? '오늘 포함' : '어제까지'} · 단위: 기기`} data={report.trend} series={[
                    { key: 'total', label: '전체', color: '#9333ea', strokeWidth: 3.5 },
                    ...SERIES,
                    ...['web', 'unknown'].filter(key => report.trend.some(row => row[key] > 0)).map(key => ({ key, label: key === 'web' ? '웹' : '미확인', color: key === 'web' ? '#d97706' : '#64748b' })),
                ]} />
            </Box>
            {includeToday && <Typography sx={{ color: '#777', fontSize: '0.8rem', lineHeight: 1.7 }}>오늘은 집계 중이에요. 오늘 하루의 중간 수치를 어제 하루 전체와 비교할 때 참고해주세요.</Typography>}
        </>}
    </Box>;
}
